import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Scrollbars } from "@/shared/components/Scroll/react-custom-scrollbars/lib";
import classNames from "classnames";
import { isAndroid, isIOS } from "react-device-detect";

import { SCROLL_DIRECTIONS } from "../../constants";
import { handleReactRef } from "../../utils/react";

import styles from "./Scroll.module.scss";

const renderEmptyElement = () => <div />;
const renderTrackVerticalDefault = (props) => {
  return <div {...props} className={classNames(styles["scrollMainTrack"], "scrollMainTrack")} />;
};
const renderThumbVerticalDefault = (props) => <div {...props} className={styles["scrollStyle"]} />;
const renderThumbHorizontalDefault = (props) => (
  <div {...props} className={styles["scrollStyle"]} />
);

const renderViewDefault = (props) => <div {...props} />;

/**
 * @description Scroll component with custom scrollbars and handling scroll stop with possible delay
 */
const Scroll = React.forwardRef(
  (
    {
      fixBrokenScrollingToTheEdgeWithSelection,
      isRenderViewFocusable,
      noHidePopups,
      children,
      onVerticalThumbMouseDown,
      onVerticalTrackMouseDown,
      onScroll,
      onScrollFrame,
      onScrollStart,
      onUpdate,
      onScrollChangeDirection,
      onScrollStop,
      onScrollStopInstant,
      stopDelay,
      className,
      renderViewClassName,
      renderViewStyle,
      fixScrollbarCoversContent,
      hasStaticPosition,
      renderView,
      hideScrollBar,
      ...props
    },
    ref
  ) => {
    const refRoot = useRef(null);
    // keep start scroll position
    const scrollTopRef = useRef();
    // keep timeout for make delayed scroll
    const timerIdScrollStop = useRef();
    // keep time for last event of stop scroll for make debounce logic
    const [scrollStop, setScrollStop] = useState();

    const onScrollStartHandler = (e) => {
      if (refRoot.current) {
        scrollTopRef.current = refRoot.current.view.scrollTop;
      }

      if (onScrollStart) {
        onScrollStart(e);
      }

      if (scrollStop && stopDelay !== 0) {
        setScrollStop(Date.now());
      }
    };

    useEffect(() => {
      if (scrollStop) {
        //use ref object allows reset event scroll stop when user scroll again
        clearTimeout(timerIdScrollStop.current);
        timerIdScrollStop.current = setTimeout(() => {
          if (scrollStop && onScrollStop) {
            let offset = 0;
            if (scrollTopRef.current && refRoot.current?.view?.scrollTop !== undefined) {
              offset = scrollTopRef.current - refRoot.current?.view?.scrollTop || 0;
            }
            onScrollStop(offset);
          }
          setScrollStop();
        }, stopDelay);
      }
      return () => {
        clearTimeout(timerIdScrollStop.current);
      };
    }, [scrollStop, stopDelay]);

    const handleScrollFrame = (event) => {
      const previousScrollStepTop = scrollTopRef.current;

      if (timerIdScrollStop.current !== undefined) {
        clearTimeout(timerIdScrollStop.current);
        timerIdScrollStop.current = undefined;
      }

      if (onScrollChangeDirection) {
        const { scrollTop } = event;
        const delta = scrollTop - previousScrollStepTop;

        if (delta === 0) {
          return;
        }

        let deltaMin = 0;

        if (refRoot.current?.view) {
          deltaMin = refRoot.current.view.clientHeight / 6;
        }

        if (scrollTop < deltaMin) {
          deltaMin = scrollTop / 2;
        }

        if (delta > 0 && delta > deltaMin) {
          onScrollChangeDirection(SCROLL_DIRECTIONS.DOWN);
        } else if (delta < 0 && delta < -deltaMin) {
          onScrollChangeDirection(SCROLL_DIRECTIONS.UP);
        }
      }
    };

    const handleScrollStop = () => {
      if (onScrollStopInstant) {
        onScrollStopInstant();
      }

      if (stopDelay > 0) {
        setScrollStop(Date.now());
      } else if (onScrollStop) {
        let offset = 0;

        if (scrollTopRef.current && refRoot.current?.view?.scrollTop !== undefined) {
          offset = scrollTopRef.current - refRoot.current?.view?.scrollTop || 0;
        }

        onScrollStop(offset);
      }
    };

    const renderViewFinal = useCallback(
      (propsRenderView) => {
        const propsRenderViewFinal = { ...propsRenderView };

        propsRenderViewFinal.className = classNames(
          "scrollbars-render-view",
          [styles["scrollbars-render-view"]],
          renderViewClassName,
          propsRenderViewFinal.className,
          {
            [styles["has-static-position"]]: hasStaticPosition,
            [styles["gap-for-scrollbar"]]: fixScrollbarCoversContent,
          }
        );

        if (renderViewStyle) {
          propsRenderViewFinal.style = { ...propsRenderView.style, ...renderViewStyle };
        }

        if (isRenderViewFocusable) {
          propsRenderViewFinal.tabIndex = 0;
        }

        return renderView
          ? renderView(propsRenderViewFinal)
          : renderViewDefault(propsRenderViewFinal);
      },
      [
        renderView,
        renderViewStyle,
        renderViewClassName,
        hasStaticPosition,
        fixScrollbarCoversContent,
        isRenderViewFocusable,
      ]
    );

    const handleRef = useCallback(
      (el) => {
        handleReactRef(ref, el);

        refRoot.current = el;
      },
      [ref]
    );

    return (
      <Scrollbars
        ref={handleRef}
        className={classNames(styles["scrollbars-wrapper"], "scrollbars-wrapper", className)}
        hideTracksWhenNotNeeded
        autoHide
        onUpdate={onUpdate}
        onScrollStart={onScrollStartHandler}
        onScrollFrame={(event) => {
          handleScrollFrame(event);
          if (onScrollFrame) {
            onScrollFrame(event);
          }
        }}
        onVerticalThumbMouseDown={onVerticalThumbMouseDown}
        onVerticalTrackMouseDown={onVerticalTrackMouseDown}
        onScrollStop={handleScrollStop}
        onScroll={onScroll}
        renderView={renderViewFinal}
        renderTrackVertical={hideScrollBar ? renderEmptyElement : renderTrackVerticalDefault}
        renderThumbVertical={hideScrollBar ? renderEmptyElement : renderThumbVerticalDefault}
        renderThumbHorizontal={hideScrollBar ? renderEmptyElement : renderThumbHorizontalDefault}
        {...props}
      >
        {children}
      </Scrollbars>
    );
  }
);

Scroll.defaultProps = {
  stopDelay: 0,
};

Scroll.propTypes = {
  className: PropTypes.string,
  renderViewClassName: PropTypes.string,
  noHidePopups: PropTypes.bool,
  hideScrollBar: PropTypes.bool,
  fixScrollbarCoversContent: PropTypes.bool,
  renderViewStyle: PropTypes.object,
  hasStaticPosition: PropTypes.bool,
  children: PropTypes.node,
  onVerticalThumbMouseDown: PropTypes.func,
  onVerticalTrackMouseDown: PropTypes.func,
  onScroll: PropTypes.func,
  onScrollFrame: PropTypes.func,
  onScrollChangeDirection: PropTypes.func,
  onScrollStart: PropTypes.func,
  onUpdate: PropTypes.func,
  onScrollStop: PropTypes.func,
  onScrollStopInstant: PropTypes.func,
  renderView: PropTypes.func,
  stopDelay: PropTypes.number,
};

export default Scroll;
